import { useForm } from '@mantine/form';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  TextInput,
  Grid,
  Text,
  Group,
} from '@mantine/core';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSaveMutation, useCalculateMutation, useCheckMortgageEligibilityMutation } from '@/services/api';
import { updateExtraDetails } from '@/data/features/proposal/proposal';
import { getMutationResult } from '@/helpers/query';
import { useTranslation } from 'react-i18next';
import { formatNumberWithCommas } from '@/helpers/string';
import { Page, ProcessHeader, Modal } from '@/components';
import { getRouteById, routeWithParams } from '@/helpers/routes';
import routes from '@/routes';
import { useIdInStoreGuard } from '@/hooks/useIdfromUrl';
import { IconAlertTriangle, IconMessageCircleQuestion } from '@tabler/icons-react';
import { IMaskInput } from 'react-imask';
import { notifications } from '@mantine/notifications';
import { updateProposalOffer } from '@/data/features/calculate/calculate';
import { toISOString } from '@/helpers/date';
import { removeNumberFormatting } from '@/helpers/number';
import ProcessButton from '@/components/ProcessButton';
import SendDetailsModal from '@/components/SendDetailsModal';
import { PROBLEM_TYPES } from '@/helpers/enums';
import MoneyBagIcon from '@/assets/images/moneyBag.svg?react'; // eslint-disable-line import/no-unresolved
import classes from './index.module.css';

const LoanAmount = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalData, setErrorModalData] = useState({ title: '', message: '' });
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const dispatch = useDispatch();
  const [apiSave] = useSaveMutation();
  const [calcApiConfig] = useCalculateMutation();
  const [apiCheckMortgageEligibility] = useCheckMortgageEligibilityMutation();

  const navigate = useNavigate();
  const { extraDetails, id } = useSelector((state) => state.proposal);
  const mortageCycleDetails = useSelector((state) => state.mortgageCycle);
  const loanOptions = useSelector((state) => state.loanOptions);

  const required = t('globals.validations.required');
  const numbers = t('globals.validations.number');
  useIdInStoreGuard();

  const {
    propertyValuation,
    loan,
    income,
    outcome,
    oldestBorrowerDOB,
    loanTypeName,
    isRecycling,
    loanTypeNameHebrew,
    currentLoanAmount,
    youngestBorrowerDOB,
    cycleMortgage,
  } = extraDetails;

  const booleanIsRecycling = isRecycling === 'yes';

  const formValidations = {
    loanAmount: (value) => {
      if (!value || typeof value !== 'string') return required;
      // Remove commas and check if it's a valid number
      const cleanValue = value.replace(/[₪,\s]/g, '');
      if (!/^\d+$/.test(cleanValue)) return numbers;
      return null;
    },
  };

  const initialValues = {
    loanAmount: formatNumberWithCommas(extraDetails.loanAmount),
  };

  const form = useForm({
    mode: 'controlled',
    validateInputOnBlur: true,
    initialValues,
    validate: formValidations,
    validateInputOnSubmit: true,
  });

  const handleNumberChange = (field) => (event) => {
    const formattedValue = formatNumberWithCommas(event.target.value);
    form.setFieldValue(field, formattedValue);
  };

  const handleContinue = async () => {
    try {
      setLoading(true);

      // Validate form
      const validation = form.validate();
      if (validation.hasErrors) {
        return;
      }
      const { loanAmount } = form.values;

      // Keep the value as a string for display purposes
      const formData = {
        ...form.values,
        loanAmount: removeNumberFormatting(loanAmount), // Keep as number
      };

      // Save data to API
      const objectToSend = {
        ...formData,
        id,
      };

      const { isError } = await getMutationResult(objectToSend, apiSave);

      if (isError) {
        notifications.show({
          title: t('globals.error.title'),
          message: t('globals.error.message'),
        });
        return;
      }

      // Save data to store
      dispatch(updateExtraDetails(formData));

      const formattedLoanAmount = removeNumberFormatting(loanAmount);
      const formattedOldestBorrowerDOB = toISOString(oldestBorrowerDOB);
      const formattedYoungestBorrowerDOB = toISOString(youngestBorrowerDOB);
      const loanType = loanOptions.find((l) => l._id === loan);

      // check if loan amount + current loan amount is eligible for mortgage
      if (booleanIsRecycling) {
        const currentLoansTotal = (mortageCycleDetails?.currentLoans || []).reduce(
          (sum, l) => sum + (l.totalAmount || 0),
          0,
        );
        const calculatedLoanAmount = formattedLoanAmount + currentLoansTotal;

        const objToCheckMortgageEligibility = {
          loanAmount: calculatedLoanAmount,
          propertyValuation,
          maxFundingPercentage: loanType.maxFundingPercentage,
        };

        const {
          data: { data },
        } = await getMutationResult(objToCheckMortgageEligibility, apiCheckMortgageEligibility);

        if (data.label === 'fundPercentageTooHigh') {
          const modalTitle = 'סכום ההלוואה המבוקש גבוה מדי';

          const errorMessage = data?.message || 'שגיאה בחישוב ההלוואה';
          setErrorModalData({
            title: modalTitle,
            message: errorMessage,
          });
          setShowErrorModal(true);
          setLoading(false);
          return;
        }
      }

      const cycleLoansWithCycleRateType = ['renovationOrExpansion', 'anyPurposeWithPropertyCollateral'];

      const objectToSendForcalculate = {
        propertyValuation,
        loan,
        loanAmount: formattedLoanAmount + (currentLoanAmount || 0),
        income,
        outcome,
        oldestBorrowerDOB: formattedOldestBorrowerDOB,
        youngestBorrowerDOB: formattedYoungestBorrowerDOB,
        loanTypeNameHebrew,
        rateType: cycleLoansWithCycleRateType.includes(loanType?.title) ? 'cycle' : 'regular',
      };

      const { currentLoans, calculationData } = mortageCycleDetails || {};
      const { totalsAfterCalculation } = calculationData || {};

      if (booleanIsRecycling) {
        let loansToAddToNewLoan = currentLoans.filter((l) => l.isBetterToChange === true);
        let loansToCycle = currentLoans.filter((l) => l.isBetterToChange === false);

        if (loanTypeName === 'anyPurposeWithPropertyCollateral') {
          loansToAddToNewLoan = currentLoans.filter((l) => l.isBetterToChange === true && l.loanPurpose === 'אחר');
          loansToCycle = currentLoans.filter((l) => l.isBetterToChange === false || l.loanPurpose === 'דיור');
        }

        const additionalLoanAmount = loansToAddToNewLoan.reduce(
          (sum, l) => sum + (l.totalAmount || 0),
          0,
        );

        objectToSendForcalculate.loanAmount += additionalLoanAmount;
        let updatedoutcome = outcome;

        if (cycleMortgage?.isMortgageRepaymentIncludedInPreviousLiabilities === 'yes') {
          loansToAddToNewLoan.forEach((l) => {
            updatedoutcome -= l.monthlyPayment || 0;
          });
          loansToCycle.forEach((l) => {
            updatedoutcome -= l.monthlyPayment || 0;
          });
        }
        objectToSendForcalculate.additionalOutcome = totalsAfterCalculation.newMonthlyPayment;
        objectToSendForcalculate.outcome = updatedoutcome;
      }

      const {
        data: calculateData,
        isError: calculateIsError,
      } = await getMutationResult(objectToSendForcalculate, calcApiConfig);

      if (calculateIsError) {
        // Check for specific error codes that need custom modals
        const errorCode = calculateData?.error?.code;
        if (errorCode === 112 || errorCode === 113) {
          let modalTitle = 'שגיאה בחישוב ההלוואה';

          // Set specific titles based on error code
          if (errorCode === 112) {
            modalTitle = 'סכום ההלוואה המבוקש גבוה מדי';
          } else if (errorCode === 113) {
            modalTitle = 'סכום ההלוואה לא תואם ליכולת ההחזר';
          }

          const errorMessage = calculateData?.error?.message || 'שגיאה בחישוב ההלוואה';
          setErrorModalData({
            title: modalTitle,
            message: errorMessage,
            calculatedPercentage: booleanIsRecycling ? (calculateData?.error?.calculatedPercentage || 0) - (currentLoanAmount || 0) || '' : calculateData?.error?.calculatedPercentage || '',
          });
          setShowErrorModal(true);
          setLoading(false);
          return;
        }

        // For other errors, show default notification
        notifications.show({
          title: t('globals.error.title'),
          message: t('globals.error.message'),
        });

        return;
      }

      const { data } = calculateData;

      dispatch(updateProposalOffer({
        ...data,
      }));

      // Navigate to next step
      navigate(routeWithParams(getRouteById(routes.newProposal.id, 'offer').path, { id }));
    } catch (error) {
      console.error('Error saving loan amount data:', error);
      notifications.show({
        title: t('globals.error.title'),
        message: t('globals.error.message'),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReturn = () => {
    if (booleanIsRecycling) {
      navigate(routeWithParams(getRouteById(routes.newProposal.id, 'mortgageCycleAction').path, { id }));
    } else if (loanTypeName === 'reverseMortgage') {
      navigate(routeWithParams(getRouteById(routes.newProposal.id, 'reverseMortgageRouteType').path, { id }));
    } else {
      navigate(routeWithParams(getRouteById(routes.newProposal.id, 'incomeLiabilities').path, { id }));
    }
  };

  let title = t('pages.loanAmount.title');
  if (booleanIsRecycling) {
    title = 'סכום התוספת המבוקשת למשכנתא הקיימת';
  }
  return (
    <ProcessHeader
      headerText={title}
      headerColor="#E8F5E8"
      headerTextColor="#2E7D32"
      subtitle={t('pages.loanAmount.subtitle')}
      showReturnButton
      infoText={t('pages.loanAmount.description')}
      onReturn={handleReturn}
      icon={MoneyBagIcon}
    >
      <form onSubmit={form.onSubmit(handleContinue)}>
        <Page.Section>
          <div className={classes.contentContainer}>
            <Grid>
              <Grid.Col span={{ base: 12, lg: 6 }}>
                <TextInput
                  withAsterisk
                  label={t('pages.loanAmount.label')}
                  placeholder="הזינו את סכום ההלוואה המבוקש"
                  key={form.key('loanAmount')}
                  {...form.getInputProps('loanAmount')}
                  onChange={handleNumberChange('loanAmount')}
                  maxLength={15}
                  component={IMaskInput}
                  scale={0}
                  min={0}
                  className={classes.loanAmountInput}
                />
              </Grid.Col>
            </Grid>
          </div>
        </Page.Section>
        <Page.Actions>
          <ProcessButton type="submit" loading={loading} />
        </Page.Actions>
      </form>

      {/* Error Modal for specific error codes */}
      <Modal
        opened={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        title={
          <Group gap="xs" align="center">
            <IconAlertTriangle size={24} color="#E91E63" />
            <Text fw={600} size="lg" c="#E91E63">
              {errorModalData.title}
            </Text>
          </Group>
        }
        centered
        size="lg"
        showClose={false}
        disableClose={true}
        styles={{
          title: {
            borderBottom: '2px solid #FFE6F0',
            paddingBottom: 'var(--mantine-spacing-md)',
            marginBottom: 'var(--mantine-spacing-lg)',
          },
        }}
      >
        <Group gap="md" align="flex-start" mb="xl">
          <div>
            <Text size="md" fw={500} mb="xs" c="#333">
              {errorModalData.message}
            </Text>
            <Text size="sm" c="#666" lh={1.6}>
              <br />
              <br />
              <strong>המלצות:</strong>
              <br />
              • יש לוודא שהנתונים שהוזנו נכונים
            </Text>
          </div>
        </Group>

        <Group justify="center" mt="xl">
          <Button
            variant="filled"
            color="pink"
            size="md"
            onClick={() => setShowErrorModal(false)}
            styles={{
              root: {
                backgroundColor: '#E91E63',
                '&:hover': {
                  backgroundColor: '#C2185B',
                },
              },
            }}
          >
            הבנתי את הבעיה
          </Button>
          <Button
            variant="outline"
            color="blue"
            onClick={() => {
              setShowErrorModal(false);
              setShowDetailsModal(true);
            }}
            loading={loading}
            rightSection={<IconMessageCircleQuestion size={16} />}
          >
            {t('pages.businessCredit.transferToConsultant')}
          </Button>
        </Group>
      </Modal>

      <SendDetailsModal
        opened={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        data={{ type: PROBLEM_TYPES.loanAmount }}
      />
    </ProcessHeader>
  );
};

export default LoanAmount;
